import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowRight, MessageSquare, Terminal, Sparkles } from "lucide-react";
import TechOrbit from "@/components/TechOrbit";

const stats = [
  { value: "2012", label: "Mulai perjalanan teknologi", accent: "from-blue-400 to-cyan-400" },
  { value: "5M+",  label: "Sistem skala jutaan pengguna", accent: "from-cyan-400 to-sky-400" },
  { value: "AI · IoT", label: "Software, hardware & integrasi", accent: "from-indigo-400 to-violet-400" },
  { value: "Nationwide", label: "Digunakan di seluruh Indonesia", accent: "from-violet-400 to-fuchsia-400" },
];

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { delay: 0.08 * i, duration: 0.6, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Hero = () => {
  const sectionRef = useRef(null);

  // Spotlight that follows the cursor
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      el.style.setProperty("--mx", `${e.clientX - r.left}px`);
      el.style.setProperty("--my", `${e.clientY - r.top}px`);
    };
    el.addEventListener("mousemove", onMove);
    return () => el.removeEventListener("mousemove", onMove);
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="home"
      ref={sectionRef}
      data-testid="hero-section"
      className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 bg-cosmic spotlight overflow-hidden"
    >
      {/* Layered backgrounds */}
      <div className="absolute inset-0 grid-bg pointer-events-none" />
      <div className="absolute inset-0 lines-diagonal opacity-50 pointer-events-none" />
      <div className="noise-overlay" />

      {/* Decorative circuit SVG corners */}
      <svg className="absolute top-24 left-6 opacity-25 hidden lg:block pointer-events-none" width="180" height="180" viewBox="0 0 180 180" fill="none">
        <path d="M0 20 H50 L70 40 H120 L140 60 H180" stroke="#60a5fa" strokeWidth="0.8" />
        <circle cx="50" cy="20" r="2.2" fill="#60a5fa" />
        <circle cx="120" cy="40" r="2.2" fill="#22d3ee" />
        <circle cx="180" cy="60" r="2.2" fill="#a78bfa" />
        <path d="M0 90 H40 L60 70 H110" stroke="#22d3ee" strokeWidth="0.6" opacity="0.7" />
      </svg>
      <svg className="absolute bottom-12 right-6 opacity-25 hidden lg:block pointer-events-none" width="180" height="180" viewBox="0 0 180 180" fill="none">
        <path d="M180 20 H130 L110 40 H60 L40 60 H0" stroke="#a78bfa" strokeWidth="0.8" />
        <circle cx="130" cy="20" r="2.2" fill="#a78bfa" />
        <circle cx="60" cy="40" r="2.2" fill="#60a5fa" />
        <circle cx="0" cy="60" r="2.2" fill="#22d3ee" />
      </svg>

      {/* floating blobs */}
      <div className="absolute -top-20 left-1/4 w-80 h-80 rounded-full bg-blue-600/15 blur-[120px]" />
      <div className="absolute top-1/3 right-0 w-96 h-96 rounded-full bg-indigo-600/15 blur-[140px]" />
      <div className="absolute bottom-0 left-10 w-72 h-72 rounded-full bg-cyan-600/10 blur-[120px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
          {/* Left content */}
          <div>
            {/* Status badge */}
            <motion.div
              variants={fade}
              initial="hidden"
              animate="show"
              custom={0}
              className="inline-flex items-center gap-2.5 pl-2 pr-4 py-1.5 rounded-full glass-strong"
              data-testid="hero-eyebrow"
            >
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-400/30">
                <span className="relative flex w-1.5 h-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                  <span className="relative inline-flex w-1.5 h-1.5 rounded-full bg-emerald-400" />
                </span>
                <span className="text-[10px] uppercase tracking-[0.18em] text-emerald-300">Available</span>
              </span>
              <span className="text-[11px] uppercase tracking-[0.22em] text-slate-300">
                Product · Technology · Solution
              </span>
            </motion.div>

            <motion.h1
              variants={fade}
              initial="hidden"
              animate="show"
              custom={1}
              className="mt-7 text-4xl sm:text-5xl lg:text-[68px] leading-[1.05] font-medium tracking-tight text-white"
              style={{ fontFamily: "Outfit" }}
              data-testid="hero-title"
            >
              <span className="block text-slate-300/90 text-[42%] uppercase tracking-[0.35em] mb-3 font-mono-display">
                — Hello, I'm
              </span>
              <span className="shimmer-text">Joneten </span>
              <span className="text-gradient-aurora">Saputra.</span>
            </motion.h1>

            <motion.p
              variants={fade}
              initial="hidden"
              animate="show"
              custom={2}
              className="mt-5 text-lg sm:text-xl text-slate-300 font-light"
              data-testid="hero-subtitle"
            >
              <span className="text-white font-medium">Product Manager</span>{" "}
              <span className="text-slate-500">×</span>{" "}
              <span className="text-white font-medium">Technical Solution Specialist</span>
            </motion.p>

            <motion.p
              variants={fade}
              initial="hidden"
              animate="show"
              custom={3}
              className="mt-6 text-base leading-relaxed text-slate-400 max-w-xl"
              data-testid="hero-description"
            >
              Berpengalaman sejak <span className="text-slate-200">2012</span> dalam merancang, mengembangkan,
              dan mengelola solusi teknologi berbasis{" "}
              <span className="text-blue-300">AI, IoT, software, hardware, energy technology</span>, dan
              manajemen produk digital — menjembatani kebutuhan bisnis dengan eksekusi teknis yang nyata.
            </motion.p>

            <motion.div
              variants={fade}
              initial="hidden"
              animate="show"
              custom={4}
              className="mt-9 flex flex-wrap items-center gap-3"
            >
              <button
                data-testid="hero-cta-primary"
                onClick={() => scrollTo("expertise")}
                className="group relative inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium shadow-[0_0_30px_rgba(59,130,246,0.35)] hover:shadow-[0_0_50px_rgba(59,130,246,0.6)] transition-shadow duration-300 overflow-hidden"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-cyan-400/0 via-cyan-300/20 to-cyan-400/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                <Sparkles className="w-4 h-4" strokeWidth={1.75} />
                Lihat Keahlian
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </button>
              <button
                data-testid="hero-cta-secondary"
                onClick={() => scrollTo("contact")}
                className="group corners inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/15 text-white hover:bg-white/[0.04] hover:border-blue-400/40 transition-colors"
              >
                <MessageSquare className="w-4 h-4 text-blue-300" strokeWidth={1.75} />
                Konsultasi Project
              </button>
            </motion.div>

            {/* Mini terminal card */}
            <motion.div
              variants={fade}
              initial="hidden"
              animate="show"
              custom={5}
              className="mt-9 max-w-md rounded-xl glass-elevated p-4 font-mono-display text-xs"
              data-testid="hero-terminal"
            >
              <div className="flex items-center gap-1.5 mb-3">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-400/70" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
                <span className="ml-2 text-[10px] uppercase tracking-[0.22em] text-slate-500">~/joneten — zsh</span>
              </div>
              <div className="text-slate-400">
                <span className="text-emerald-400">$</span> whoami
              </div>
              <div className="text-slate-200 leading-relaxed">
                product_manager · technical_solution
              </div>
              <div className="text-slate-400 mt-1">
                <span className="text-emerald-400">$</span> stack <span className="text-slate-500">--list</span>
              </div>
              <div className="text-blue-300 leading-relaxed">
                ai · iot · software · hardware · smart_energy
                <span className="inline-block w-1.5 h-3 bg-blue-300 align-middle ml-1 animate-blink" />
              </div>
            </motion.div>

            {/* Stats */}
            <motion.div
              variants={fade}
              initial="hidden"
              animate="show"
              custom={6}
              className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4"
              data-testid="hero-stats"
            >
              {stats.map((s, idx) => (
                <div
                  key={idx}
                  data-testid={`hero-stat-${idx}`}
                  className="group relative rounded-2xl p-4 sm:p-5 glass corners hover:border-blue-400/40 hover:bg-white/[0.04] transition-all duration-300 overflow-hidden"
                >
                  <div className={`text-xl sm:text-2xl font-medium text-white bg-clip-text text-transparent bg-gradient-to-r ${s.accent}`} style={{ fontFamily: "Outfit" }}>
                    {s.value}
                  </div>
                  <div className="mt-1.5 text-[11px] sm:text-xs text-slate-400 leading-snug">
                    {s.label}
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right visual */}
          <motion.div
            variants={fade}
            initial="hidden"
            animate="show"
            custom={3}
            className="relative animate-float-slow"
          >
            <TechOrbit />
            {/* floating label cards */}
            <div className="hidden md:flex absolute -left-2 top-8 items-center gap-2 px-3 py-2 rounded-xl glass-strong text-[11px] text-slate-300">
              <Terminal className="w-3.5 h-3.5 text-cyan-300" strokeWidth={1.5} />
              <span className="font-mono-display">uptime · 12+ years</span>
            </div>
            <div className="hidden md:flex absolute right-0 bottom-10 items-center gap-2 px-3 py-2 rounded-xl glass-strong text-[11px] text-slate-300">
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-blink" />
              <span className="font-mono-display">streaming insights…</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* bottom fade */}
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-[#060a1a]" />
    </section>
  );
};

export default Hero;
